import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Search, SlidersHorizontal, X, ArrowUpDown, Copy, Check, Link2,
  LayoutGrid, List, ChevronRight, Frown as HorseIcon,
} from "lucide-react";
import { getHorses, getHorsesMeta, syncSmartsheet, mediaRawUrl } from "@/lib/api";
import { RecordTypeTabs, useSelection, SelectToggle, BulkBar, Selectable, typeOf } from "@/components/RecordTypes";
import { listProspects } from "@/lib/prospectApi";
import { ProspectsPanel } from "@/components/prospects/ProspectsPanel";
import { val, fmtDate, dateBucket, isEmpty } from "@/lib/format";
import { MobileMenu, MobileTabBar, DesktopNav } from "@/components/AppNav";
import { AppHeader, Chip, Eyebrow } from "@/components/brand/AppHeader";
import { BrandName, useBrand } from "@/lib/brand";
import { StatusChips } from "@/components/WorkflowStatus";
import { copyText, FacebookIcon } from "@/lib/clipboard";
import {
  MAIN_FILTERS, DEFAULT_MAIN_FILTER, matchesMain, CONTRACT_LABELS, AD_LABELS, SORTS, sortHorses,
} from "@/lib/workflow";
import { TestBadge } from "@/components/TestMode";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";

const ALL = "__all__";
const VIEW_KEY = "bahs-horse-view";

const initialView = () => {
  const saved = localStorage.getItem(VIEW_KEY);
  if (saved === "grid" || saved === "list") return saved;
  return window.innerWidth < 768 ? "list" : "grid";
};

const thumbUrl = (h) => {
  const imgs = (h.media || []).filter((m) => m.kind === "image");
  if (!imgs.length) return null;
  const m = [...imgs].sort((a, b) => (a.ad_order || 999) - (b.ad_order || 999))[0];
  return m.external_url || mediaRawUrl(h.id, m.smartsheet_attachment_id, m.rotation);
};

const initialsOf = (name = "") => name.split(" ").filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "BA";

// Sale timing pill: the single most useful fact when scanning horses.
const SALE_TONE = { active: "bg-emerald-500", upcoming: "bg-sky-500", closed: "bg-stone-400" };
// The official schedule lives in sale_info (mirrored from the application); horse_info dates are a legacy fallback.
const schedHi = (h) => ({ ...(h.horse_info || {}),
  sale_close_date: h.sale_info?.sale_close_date || h.horse_info?.sale_close_date,
  preview_start_date: h.horse_info?.preview_start_date || h.sale_info?.sale_start_date });
const saleInfo = (hi, short) => {
  const b = dateBucket(hi);
  const trim = (d) => (short && d ? d.replace(/,? \d{4}$/, "") : d);
  const close = trim(fmtDate(hi.sale_close_date));
  const prev = trim(fmtDate(hi.preview_start_date));
  if (b === "active") return { tone: b, label: close ? `${short ? "Closes" : "Live · closes"} ${close}` : "Sale live" };
  if (b === "upcoming") return { tone: b, label: prev ? `Preview ${prev}` : "Upcoming" };
  if (b === "closed") return { tone: b, label: close ? `Closed ${close}` : "Closed" };
  return null;
};

function SalePill({ hi, testId, className = "", short = false }) {
  const s = saleInfo(hi, short);
  if (!s) return null;
  return (
    <span data-testid={testId} className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full text-[11px] font-semibold ${className}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${SALE_TONE[s.tone]}`} />{s.label}
    </span>
  );
}

function HorsePhoto({ h, className, fallback }) {
  const [failed, setFailed] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const src = thumbUrl(h);
  if (!src || failed) return fallback;
  return <img src={src} alt={h.horse_info?.horse_name || "Horse"} loading="lazy" decoding="async" onLoad={() => setLoaded(true)} onError={() => setFailed(true)} className={`${className} ${loaded ? "" : "animate-pulse bg-stone-100"}`} />;
}

function Monogram({ name = "", type = "" }) {
  return (
    <div className="relative h-full w-full bg-gradient-to-br from-stone-200 via-stone-100 to-stone-50 flex items-center justify-center">
      <span className="font-display text-5xl font-extrabold text-stone-400 tracking-tight">{initialsOf(name)}</span>
      {type && <Eyebrow className="absolute top-3 left-3 !text-stone-600">{type}</Eyebrow>}
    </div>
  );
}

const metaLine = (hi) => [hi.gender, hi.year_born && `Born ${hi.year_born}`, hi.horse_type].filter((x) => !isEmpty(x)).join(" · ");

export default function HorseList() {
  const navigate = useNavigate();
  const brand = useBrand();
  const [horses, setHorses] = useState([]);
  const [allHorses, setAllHorses] = useState([]);
  const [prospects, setProspects] = useState([]);
  const upsertProspect = (p) => setProspects((rs) => (rs.some((r) => r.id === p.id) ? rs.map((r) => (r.id === p.id ? p : r)) : [p, ...rs]));
  const [recordType, setRecordTypeView] = useState("horse_sale");
  const [mode, setMode] = useState("live");
  const sel = useSelection();
  const [meta, setMeta] = useState({ statuses: [], horse_types: [], customers: [] });
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [view, setViewState] = useState(initialView);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const [q, setQ] = useState("");
  const [main, setMain] = useState(DEFAULT_MAIN_FILTER);
  const [sort, setSort] = useState("sale_date");
  const [contract, setContract] = useState(ALL);
  const [adStatus, setAdStatus] = useState(ALL);
  const [type, setType] = useState(ALL);
  const [customer, setCustomer] = useState(ALL);
  const [timing, setTiming] = useState(ALL);

  const setView = (v) => { setViewState(v); localStorage.setItem(VIEW_KEY, v); };
  const activeCount = [contract, adStatus, type, customer, timing].filter((f) => f !== ALL).length
    + (main !== DEFAULT_MAIN_FILTER ? 1 : 0);
  const sheetCount = [contract, adStatus, type, customer, timing].filter((f) => f !== ALL).length
    + (mode !== "live" ? 1 : 0) + (recordType !== "horse_sale" ? 1 : 0);
  const clearFilters = () => {
    setMain(DEFAULT_MAIN_FILTER); setContract(ALL); setAdStatus(ALL);
    setType(ALL); setCustomer(ALL); setTiming(ALL); setQ("");
  };

  const load = async () => {
    setLoading(true);
    try {
      const [h, m, p] = await Promise.all([getHorses("all", mode), getHorsesMeta(), listProspects().catch(() => [])]);
      setAllHorses(h);
      setProspects(p.filter((x) => mode === "all" || !!x.is_test === (mode === "test")));
      setMeta(m);
    } catch (e) {
      toast.error("Could not load horses");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [mode]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    setHorses(recordType === "all" ? allHorses : allHorses.filter((h) => typeOf(h) === recordType));
  }, [allHorses, recordType]);
  useEffect(() => {
    document.body.style.overflow = filtersOpen && window.innerWidth < 1024 ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [filtersOpen]);
  const typeCounts = useMemo(() => {
    const c = { all: allHorses.length };
    allHorses.forEach((h) => { c[typeOf(h)] = (c[typeOf(h)] || 0) + 1; });
    c.prospect = (c.prospect || 0) + prospects.length;
    return c;
  }, [allHorses, prospects]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await syncSmartsheet();
      toast.success(
        `Synced from Smartsheet — ${res.created} new, ${res.updated} updated`
      );
      await load();
    } catch (e) {
      toast.error("Smartsheet sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const baseFiltered = useMemo(() => {
    return horses.filter((h) => {
      const hi = schedHi(h);
      const si = h.seller_info || {};
      const ws = h.workflow_status || {};
      if (contract !== ALL && ws.contract !== contract) return false;
      if (adStatus !== ALL && ws.ad !== adStatus) return false;
      if (type !== ALL && hi.horse_type !== type) return false;
      if (customer !== ALL && si.customer_name !== customer) return false;
      if (timing !== ALL && dateBucket(hi) !== timing) return false;
      if (q.trim()) {
        const hay = [
          hi.horse_name, si.customer_name, hi.horse_type, hi.gender,
          hi.sire, hi.dam, hi.registered_name,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!hay.includes(q.trim().toLowerCase())) return false;
      }
      return true;
    });
  }, [horses, q, contract, adStatus, type, customer, timing]);

  const filtered = useMemo(
    () => sortHorses(baseFiltered.filter((h) => recordType !== "horse_sale" || matchesMain(h.workflow_status, main)), sort, main),
    [baseFiltered, main, sort, recordType]
  );
  const mainCounts = useMemo(() => Object.fromEntries(
    MAIN_FILTERS.map(([k]) => [k, baseFiltered.filter((h) => matchesMain(h.workflow_status, k)).length])
  ), [baseFiltered]);

  const lastSynced = horses.length
    ? fmtDate(horses[0].last_synced_date) || ""
    : "";

  const trig = "h-11 lg:h-10 rounded-full border-stone-300 bg-white px-4 text-sm lg:text-xs text-stone-800";
  const viewBtn = (v) => `h-9 w-10 lg:h-8 lg:w-9 flex items-center justify-center rounded-full transition-colors ${view === v ? "bg-stone-700 text-white" : "text-stone-600 hover:text-stone-900 hover:bg-stone-100"}`;

  return (
    <div className="min-h-screen pb-24 md:pb-12 overflow-x-clip">
      <AppHeader title="Horse Ads">
        <MobileMenu onSynced={load} />
        <DesktopNav onSynced={load} />
      </AppHeader>

      {/* Toolbar: search + filter sheet trigger, then the status row. Everything else lives in the filter sheet on mobile. */}
      <section data-testid="horse-toolbar" className="max-w-7xl mx-auto px-4 sm:px-8 pt-4 md:pt-8">
        <div className="hidden md:flex items-end justify-between gap-6 mb-6">
          <div>
            <Eyebrow><BrandName /> · Ad Builder{lastSynced ? ` · Synced ${lastSynced}` : ""}</Eyebrow>
            <h2 className="mt-2 font-display text-4xl lg:text-5xl font-bold uppercase tracking-tight text-stone-900 leading-[0.95]">
              Horse Ads <span className="font-accent normal-case text-stone-500 text-3xl lg:text-4xl tracking-normal">in the barn.</span>
            </h2>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative flex-1 min-w-0">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-500" />
            <Input
              data-testid="horse-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search horses or sellers"
              className="pl-11 pr-10 h-11 rounded-full text-base lg:text-sm border-stone-300 bg-white text-stone-900 shadow-sm focus-visible:ring-1 focus-visible:ring-stone-500 focus-visible:ring-offset-0"
            />
            {q && (
              <button type="button" data-testid="clear-search-button" onClick={() => setQ("")} aria-label="Clear search"
                className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 inline-flex items-center justify-center rounded-full text-stone-600 hover:text-stone-900 hover:bg-stone-100">
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <button
            type="button"
            data-testid="toggle-filters-button"
            onClick={() => setFiltersOpen((o) => !o)}
            className="lg:hidden relative h-11 w-11 shrink-0 inline-flex items-center justify-center rounded-full border border-stone-300 bg-white text-stone-900 shadow-sm"
            aria-expanded={filtersOpen}
            aria-label="Filters, sort and view"
          >
            <SlidersHorizontal className="h-[18px] w-[18px]" />
            {sheetCount > 0 && (
              <span data-testid="active-filter-count" className="absolute -top-1 -right-1 inline-flex items-center justify-center h-5 min-w-[20px] px-1 rounded-full bg-stone-700 text-white text-[11px] font-bold">
                {sheetCount}
              </span>
            )}
          </button>
        </div>

        {filtersOpen && (
          <button type="button" aria-label="Close filters" data-testid="filter-backdrop" onClick={() => setFiltersOpen(false)}
            className="lg:hidden fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm animate-in fade-in-0" />
        )}
        <div
          data-testid="filter-panel"
          className={`${filtersOpen ? "fixed animate-in slide-in-from-bottom duration-300" : "hidden"} lg:static lg:block inset-x-0 bottom-0 z-[60] max-h-[85vh] overflow-y-auto lg:overflow-visible rounded-t-3xl lg:rounded-none border-t border-stone-200 lg:border-0 bg-white lg:bg-transparent shadow-2xl lg:shadow-none px-5 pt-3 pb-[calc(1.25rem+env(safe-area-inset-bottom))] lg:p-0 lg:mt-3`}
        >
          <div className="lg:hidden">
            <div className="mx-auto mb-4 h-1 w-10 rounded-full bg-stone-300" />
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-display text-lg font-bold uppercase tracking-[0.1em] text-stone-900">Filter &amp; sort</h3>
              <button type="button" data-testid="filter-close-button" onClick={() => setFiltersOpen(false)} aria-label="Close"
                className="h-10 w-10 inline-flex items-center justify-center rounded-full border border-stone-300 text-stone-800 hover:bg-stone-100"><X className="h-4 w-4" /></button>
            </div>
          </div>

          <div className="space-y-6 lg:space-y-3">
            <div className="lg:flex lg:items-center lg:justify-between lg:gap-4 space-y-6 lg:space-y-0">
              <div className="lg:order-2 lg:shrink-0">
                <Eyebrow className="mb-2.5 lg:hidden">Records</Eyebrow>
                <div data-testid="mode-filter" role="tablist" className="inline-flex items-center gap-1 p-1 rounded-full border border-stone-300 bg-white">
                  {[["live", "Live"], ["test", "TEST"], ["all", "All"]].map(([k, l]) => (
                    <Chip key={k} role="tab" active={mode === k} tone={k === "test" ? "test" : undefined} testId={`mode-filter-${k}`}
                      onClick={() => { setMode(k); sel.reset(); }} className={`!h-8 lg:!h-7 ${mode === k ? "" : "!border-transparent !bg-transparent"}`}>{l}</Chip>
                  ))}
                </div>
                {mode !== "live" && <p data-testid="mode-filter-note" className="mt-2 text-[11px] text-stone-600">TEST ads are never in LIVE counts or the LIVE website feed.</p>}
              </div>
              <div className="min-w-0 lg:order-1">
                <Eyebrow className="mb-2.5 lg:hidden">Record type</Eyebrow>
                <RecordTypeTabs className="flex-wrap lg:flex-nowrap" value={recordType} onChange={(k) => { setRecordTypeView(k); sel.reset(); }} counts={typeCounts} />
              </div>
            </div>

            <div className="lg:flex lg:items-center lg:gap-2 space-y-6 lg:space-y-0">
              <div className="lg:flex-1 min-w-0">
                <Eyebrow className="mb-2.5 lg:hidden">Filter by</Eyebrow>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-2">
                  <Select value={contract} onValueChange={setContract}>
                    <SelectTrigger data-testid="filter-contract" className={trig}>
                      <SelectValue placeholder="Contract" />
                    </SelectTrigger>
                    <SelectContent className="z-[70]">
                      <SelectItem value={ALL}>All Contracts</SelectItem>
                      {Object.entries(CONTRACT_LABELS).map(([k, l]) => (
                        <SelectItem key={k} value={k}>{l}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={adStatus} onValueChange={setAdStatus}>
                    <SelectTrigger data-testid="filter-ad-status" className={trig}>
                      <SelectValue placeholder="Ad Status" />
                    </SelectTrigger>
                    <SelectContent className="z-[70]">
                      <SelectItem value={ALL}>All Ad Statuses</SelectItem>
                      {Object.entries(AD_LABELS).map(([k, l]) => (
                        <SelectItem key={k} value={k}>{l}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={type} onValueChange={setType}>
                    <SelectTrigger data-testid="filter-type" className={trig}>
                      <SelectValue placeholder="Type" />
                    </SelectTrigger>
                    <SelectContent className="z-[70]">
                      <SelectItem value={ALL}>All Types</SelectItem>
                      {meta.horse_types.map((t) => (
                        <SelectItem key={t} value={t}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={timing} onValueChange={setTiming}>
                    <SelectTrigger data-testid="filter-timing" className={trig}>
                      <SelectValue placeholder="Dates" />
                    </SelectTrigger>
                    <SelectContent className="z-[70]">
                      <SelectItem value={ALL}>All Dates</SelectItem>
                      <SelectItem value="upcoming">Preview Upcoming</SelectItem>
                      <SelectItem value="active">Sale Active</SelectItem>
                      <SelectItem value="closed">Sale Closed</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={customer} onValueChange={setCustomer}>
                    <SelectTrigger data-testid="filter-customer" className={trig}>
                      <SelectValue placeholder="Customer" />
                    </SelectTrigger>
                    <SelectContent className="z-[70] max-h-72">
                      <SelectItem value={ALL}>All Customers</SelectItem>
                      {meta.customers.map((c) => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Eyebrow className="mb-2.5 lg:hidden">Sort &amp; view</Eyebrow>
                <div className="flex items-center gap-2">
                  <Select value={sort} onValueChange={setSort}>
                    <SelectTrigger data-testid="sort-select" className={`${trig} flex-1 lg:flex-none lg:w-[200px]`}>
                      <ArrowUpDown className="h-3.5 w-3.5 mr-1.5 text-stone-500 shrink-0" />
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="z-[70]">
                      {SORTS.map(([k, l]) => (
                        <SelectItem key={k} value={k} data-testid={`sort-${k}`}>{l}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div data-testid="view-toggle" className="flex items-center gap-0.5 border border-stone-300 bg-white rounded-full p-1 shrink-0">
                    <button data-testid="view-list" onClick={() => setView("list")} aria-label="List view" className={viewBtn("list")}>
                      <List className="h-4 w-4" />
                    </button>
                    <button data-testid="view-grid" onClick={() => setView("grid")} aria-label="Card view" className={viewBtn("grid")}>
                      <LayoutGrid className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:hidden mt-8 grid grid-cols-2 gap-2">
            <Button data-testid="filter-reset-button" variant="outline" onClick={() => { clearFilters(); setMode("live"); setRecordTypeView("horse_sale"); sel.reset(); }}
              className="h-12 rounded-full border-stone-300 bg-white text-stone-800">Reset</Button>
            <Button data-testid="filter-apply-button" onClick={() => setFiltersOpen(false)} className="h-12 rounded-full bg-primary hover:bg-amber-900 text-white font-semibold">
              Show {loading ? "" : filtered.length} {filtered.length === 1 ? "horse" : "horses"}
            </Button>
          </div>
        </div>

        {recordType === "horse_sale" && (
          <div data-testid="main-status-filter" role="tablist" className="mt-3 -mx-4 px-4 sm:mx-0 sm:px-0 flex gap-1.5 overflow-x-auto hide-scrollbar">
            {MAIN_FILTERS.map(([k, l]) => (
              <Chip key={k} role="tab" active={main === k} testId={`main-filter-${k}`} onClick={() => setMain(k)} count={loading ? "–" : mainCounts[k]}>{l}</Chip>
            ))}
          </div>
        )}

        <div className="mt-2 flex items-center justify-between gap-2 text-xs text-stone-600 min-h-[36px]">
          <span data-testid="result-count" className="truncate tabular-nums">
            {loading ? "Loading…" : <><span className="text-stone-900 font-bold">{filtered.length}</span> of {horses.length} records</>}
          </span>
          <div className="flex items-center gap-1 shrink-0">
            {(activeCount > 0 || q) && (
              <button
                data-testid="clear-filters-button"
                onClick={clearFilters}
                className="inline-flex items-center gap-1 min-h-[36px] px-2 text-stone-900 font-semibold hover:underline"
              >
                <X className="h-3.5 w-3.5" /> Clear
                <span className="hidden lg:inline">{activeCount > 0 ? ` (${activeCount})` : ""}</span>
              </button>
            )}
            <SelectToggle sel={sel} />
          </div>
        </div>
      </section>

      <main className="max-w-7xl mx-auto px-4 sm:px-8 pt-2 md:pt-3">
        {recordType === "prospect" && !loading && <ProspectsPanel rows={prospects} onChange={upsertProspect} isTest={mode === "test"} />}
        {recordType === "prospect" && !loading && horses.length > 0 && (
          <h2 data-testid="prospect-imported-heading" className="font-display text-base md:text-lg font-bold text-stone-900 mb-2">Imported records classified as Prospect / Lead</h2>
        )}
        {recordType === "prospect" && !loading && horses.length === 0 ? null : loading ? (
          <div className={view === "list" ? "space-y-2" : "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6"}>
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className={view === "list" ? "h-[84px] rounded-2xl bg-stone-200" : "h-80 rounded-2xl bg-stone-200"} />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 md:py-24 text-stone-600 rounded-2xl border border-dashed border-stone-300 bg-white">
            <HorseIcon className="h-10 w-10 mx-auto mb-3 text-stone-400" />
            <p className="font-display uppercase tracking-[0.1em] text-stone-900 font-bold">No horses match</p>
            <p className="font-accent text-xl text-stone-600 mt-1">try loosening the reins on your filters.</p>
            {horses.length === 0 && brand?.primary ? (
              <Button onClick={handleSync} disabled={syncing} className="mt-6 h-11 bg-primary hover:bg-amber-900 text-white rounded-full px-6 font-semibold">
                Sync from Smartsheet
              </Button>
            ) : (
              <Button data-testid="empty-clear-filters" variant="outline" onClick={clearFilters} className="mt-6 h-11 rounded-full border-stone-300 bg-white text-stone-900 px-6">
                Clear filters
              </Button>
            )}
          </div>
        ) : view === "grid" ? (
          <div data-testid="horse-grid" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {filtered.map((h, i) => (
              <Selectable key={h.id} sel={sel} h={h}><HorseCard h={h} i={i} onOpen={() => navigate(`/horse/${h.id}`)} /></Selectable>
            ))}
          </div>
        ) : (
          <div data-testid="horse-list" className="rounded-2xl border border-stone-200 bg-white shadow-sm divide-y divide-stone-100 overflow-hidden">
            {filtered.map((h, i) => (
              <Selectable key={h.id} sel={sel} h={h}><HorseRow h={h} i={i} onOpen={() => navigate(`/horse/${h.id}`)} /></Selectable>
            ))}
          </div>
        )}
      </main>
      <BulkBar sel={sel} visible={filtered} onDone={load} />
      <MobileTabBar />
    </div>
  );
}

function HorseCard({ h, i, onOpen }) {
  const hi = schedHi(h);
  const si = h.seller_info || {};
  return (
    <article
      data-testid={`horse-card-${i}`}
      onClick={onOpen}
      className="group relative h-full flex flex-col overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-sm hover:shadow-lg hover:border-stone-300 hover:-translate-y-0.5 transition-[transform,box-shadow,border-color] duration-300 cursor-pointer animate-fade-up"
      style={{ animationDelay: `${Math.min(i, 8) * 40}ms` }}
    >
      <div className="relative aspect-[16/10] overflow-hidden bg-stone-100">
        <HorsePhoto h={h} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
          fallback={<Monogram name={hi.horse_name} type={hi.horse_type} />} />
        <div className="absolute top-3 left-3 right-3 flex items-start justify-between gap-2">
          <span>{h.is_test && <TestBadge testId={`card-test-${i}`} />}</span>
          <SalePill hi={hi} testId={`card-sale-${i}`} className="bg-white/95 shadow-sm px-2.5 py-1 text-stone-900" />
        </div>
      </div>
      <div className="flex-1 flex flex-col p-4">
        <h3 className="font-display text-lg font-bold uppercase tracking-wide text-stone-900 leading-tight break-words">
          {val(hi.horse_name, "Unnamed Horse")}
        </h3>
        <p className="mt-0.5 text-sm font-medium text-stone-700 truncate">{val(si.customer_name)}</p>
        {metaLine(hi) && <p className="mt-1 text-xs text-stone-600 truncate">{metaLine(hi)}</p>}
        <div data-testid={`card-status-${i}`} className="mt-2.5 flex flex-wrap gap-1.5">
          <StatusChips ws={h.workflow_status} />
        </div>
        <div className="mt-auto pt-3 flex items-center justify-between border-t border-stone-100 mt-4">
          {fbUrl(h) ? (
            <div className="flex items-center gap-1 -ml-2">
              <a
                href={fbUrl(h)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                data-testid={`ad-link-${i}`}
                className="inline-flex items-center gap-1 min-h-[40px] px-2 text-xs font-semibold text-stone-800 hover:text-stone-950"
              >
                <Link2 className="h-3.5 w-3.5" /> View ad
              </a>
              <FbCopyButton h={h} testId={`card-fb-${i}`} />
            </div>
          ) : (
            <span className="text-xs text-stone-500">No ad yet</span>
          )}
          <span className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.16em] font-bold text-stone-900 transition-colors">
            Open <ChevronRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
          </span>
        </div>
      </div>
    </article>
  );
}

function HorseRow({ h, i, onOpen }) {
  const hi = schedHi(h);
  const si = h.seller_info || {};
  const name = val(hi.horse_name, "Unnamed Horse");
  return (
    <div
      data-testid={`horse-row-${i}`}
      onClick={onOpen}
      className="group flex items-center gap-3 sm:gap-4 px-3 sm:px-4 py-3 hover:bg-stone-50 active:bg-stone-100 cursor-pointer transition-colors"
    >
      <HorsePhoto h={h} className="h-16 w-16 sm:h-14 sm:w-14 shrink-0 rounded-xl object-cover bg-stone-100"
        fallback={<div className="h-16 w-16 sm:h-14 sm:w-14 shrink-0 rounded-xl bg-stone-100 border border-stone-200 text-stone-500 font-display font-bold flex items-center justify-center">{initialsOf(hi.horse_name)}</div>} />
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline justify-between gap-2 min-w-0">
          <div className="flex items-center gap-1.5 min-w-0">
            {h.is_test && <TestBadge testId={`row-test-${i}`} />}
            <span className="font-bold text-[15px] text-stone-900 truncate">{name}</span>
          </div>
          <SalePill hi={hi} short testId={`row-sale-${i}`} className="shrink-0 text-stone-700 sm:hidden" />
        </div>
        <div className="text-xs text-stone-600 truncate">
          {val(si.customer_name)}
          <span className="hidden sm:inline">{metaLine(hi) ? ` · ${metaLine(hi)}` : ""}</span>
        </div>
        <div data-testid={`row-status-${i}`} className="mt-1.5 flex flex-wrap gap-1">
          <StatusChips ws={h.workflow_status} compact />
        </div>
      </div>
      <SalePill hi={hi} className="hidden sm:inline-flex w-44 shrink-0 text-stone-700" />
      {fbUrl(h) && <FbCopyButton h={h} testId={`row-fb-${i}`} />}
      <ChevronRight className="hidden sm:block h-4 w-4 text-stone-500 shrink-0 transition-transform group-hover:translate-x-0.5" />
    </div>
  );
}

const fbUrl = (h) => h.publishing_destinations?.facebook_group?.url || null;

function FbCopyButton({ h, testId }) {
  const [copied, setCopied] = useState(false);
  const stop = (e) => e.stopPropagation();
  const btn = "h-10 w-9 sm:h-9 sm:w-9 shrink-0 inline-flex items-center justify-center rounded-full transition-colors";
  const copy = async (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (await copyText(fbUrl(h), "Copied!")) {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  };
  return (
    <div className="flex items-center shrink-0" onClick={stop}>
      <a
        data-testid={`${testId}-open`}
        href={fbUrl(h)}
        target="_blank"
        rel="noopener noreferrer"
        title="Open Facebook Post"
        aria-label="Open Facebook Post"
        onClick={stop}
        className={`${btn} text-[#1877F2] hover:bg-blue-50`}
      >
        <FacebookIcon className="h-[18px] w-[18px]" />
      </a>
      <button
        data-testid={`${testId}-copy`}
        type="button"
        title="Copy Facebook Link"
        aria-label="Copy Facebook Link"
        onClick={copy}
        className={`${btn} ${copied ? "text-emerald-700" : "text-stone-600"} hover:bg-stone-100`}
      >
        {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
      </button>
    </div>
  );
}
